/**
 * Product Card Carousel
 * Extends the native <product-card> custom element with image carousel functionality.
 * - Desktop: arrows on hover, auto-advance to image 2 on hover-enter, max 4 images
 * - Mobile: touch swipe, no arrows
 */

(function () {
  'use strict';

  // Wait until the DOM is ready and any existing custom element definitions have run
  function init() {
    const cards = document.querySelectorAll('product-card[data-carousel]');
    cards.forEach(card => new ProductCardCarousel(card));
  }

  class ProductCardCarousel {
    constructor(card) {
      this.card = card;
      this.figure = card.querySelector('.product-card__figure');
      this.mediaLink = card.querySelector('.product-card__media');

      if (!this.figure || !this.mediaLink) return;

      this.images = Array.from(card.querySelectorAll('.product-card__carousel-image'));
      this.total = this.images.length;

      if (this.total < 2) return;

      this.current = 0;
      this.isPointerFine = window.matchMedia('(pointer: fine)').matches;

      this._buildDots();

      if (this.isPointerFine) {
        this._buildArrows();
        this._bindDesktop();
      } else {
        this._bindTouch();
      }
    }

    // ─── Dots ────────────────────────────────────────────────────────────────

    _buildDots() {
      const nav = document.createElement('div');
      nav.className = 'product-card__carousel-dots';

      this.dots = this.images.map((_, i) => {
        const dot = document.createElement('span');
        dot.className = 'product-card__carousel-dot' + (i === 0 ? ' is-active' : '');
        nav.appendChild(dot);
        return dot;
      });

      this.figure.appendChild(nav);
    }

    _updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.current);
      });
    }

    // ─── Arrows (desktop only) ────────────────────────────────────────────────

    _buildArrows() {
      this.prevBtn = this._createArrow('prev');
      this.nextBtn = this._createArrow('next');
      this.figure.appendChild(this.prevBtn);
      this.figure.appendChild(this.nextBtn);
    }

    _createArrow(dir) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `product-card__carousel-arrow product-card__carousel-arrow--${dir}`;
      btn.setAttribute('aria-label', dir === 'prev' ? 'Vorheriges Bild' : 'Nächstes Bild');
      // Use a simple SVG chevron so we are not dependent on theme icon snippets in JS
      btn.innerHTML = dir === 'prev'
        ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>`
        : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>`;
      return btn;
    }

    // ─── Desktop bindings ─────────────────────────────────────────────────────

    _bindDesktop() {
      // Auto-advance to image 2 on hover enter
      this.card.addEventListener('mouseenter', () => {
        if (this.current === 0) this._goTo(1);
      });

      // Return to image 1 on hover leave
      this.card.addEventListener('mouseleave', () => {
        this._goTo(0);
      });

      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this._goTo((this.current - 1 + this.total) % this.total);
      });

      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this._goTo((this.current + 1) % this.total);
      });
    }

    // ─── Touch bindings (mobile) ──────────────────────────────────────────────

    _bindTouch() {
      let startX = 0;
      let startY = 0;
      let isDragging = false;

      this.mediaLink.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = false;
      }, { passive: true });

      this.mediaLink.addEventListener('touchmove', (e) => {
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;
        // Only hijack clearly horizontal swipes
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
          isDragging = true;
          e.preventDefault();
        }
      }, { passive: false });

      this.mediaLink.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) {
          if (dx < 0) {
            this._goTo((this.current + 1) % this.total);
          } else {
            this._goTo((this.current - 1 + this.total) % this.total);
          }
        }
      });
    }

    // ─── Core: go to index ────────────────────────────────────────────────────

    _goTo(index) {
      if (index === this.current) return;
      this.images[this.current].classList.remove('is-active');
      this.current = index;
      this.images[this.current].classList.add('is-active');
      this._updateDots();
    }
  }

  // Initialise after DOM content loaded (or immediately if already loaded)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run for dynamically injected cards (e.g. infinite scroll, quick-view)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches('product-card[data-carousel]')) {
          new ProductCardCarousel(node);
        }
        node.querySelectorAll && node.querySelectorAll('product-card[data-carousel]').forEach(card => {
          new ProductCardCarousel(card);
        });
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();